import React, { createContext, useContext, useEffect, useState } from "react";
import Joyride from "react-joyride";
import { desktopSteps, mobileSteps } from "./TourSteps";
import "../assets/css/TourProvider.css";
import { useLocation } from "../hooks/useRouter";
import { useEventEmitter } from "../contexts/EventEmmiter";
import { STORAGE_KEYS } from "../constant/keyConstants";

const TourContext = createContext();
export const useTour = () => useContext(TourContext);

export default function TourProvider({ children }) {
  const [run, setRun] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  // eslint-disable-next-line 
  const { emit, subscribe } = useEventEmitter();
  const startTour = () => setRun(true);
  const stopTour = () => setRun(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile((prev) => {
        if (prev !== isMobile) {
          stopTour();
          return isMobile;
        }
        return prev;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent body scroll while tour is active
  useEffect(() => {
    if (run) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [run]);

  // End tour when clicked outside Joyride popup
  useEffect(() => {
    if (!run) return;

    const handleClickOutside = (event) => {
      const tooltip = document.querySelector(".react-joyride__tooltip");
      if (tooltip && !tooltip.contains(event.target)) {
        stopTour();
        localStorage.removeItem(STORAGE_KEYS.SHOW_TOUR_GUIDE);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [run]);

  // Auto-start tour on login
  useEffect(() => {
    const shouldShow = localStorage.getItem(STORAGE_KEYS.SHOW_TOUR_GUIDE);

    // Only show if triggered by login AND user hasn't completed it before
    if (shouldShow === "false" && location.pathname === "/chat") {
      // Check if policy popup is open
      const isPolicyOpen = localStorage.getItem("isPolicy") === "false";

      if (isPolicyOpen) {
        // Wait for policy popup to close
        let timer;
        const unsubscribe = subscribe("policyPopupClosed", () => {
          // Policy popup closed, now show tour guide
          timer = setTimeout(() => setRun(true), 600);
        });
        return () => {
          unsubscribe();
          if (timer) clearTimeout(timer);
        };
      } else {
        // Policy popup is not open, show tour guide directly
        const timer = setTimeout(() => setRun(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, subscribe]);

  return (
    <TourContext.Provider value={{ startTour, stopTour }}>
      {children}

      <Joyride
        steps={(isMobile ? mobileSteps : desktopSteps).map(step => ({
          ...step,
          disableBeacon: true,
        }))}
        continuous
        scrollToFirstStep={false}
        showSkipButton
        showCloseButton={false}
        disableScrollParentFix={true}
        showProgress={false}
        spotlightPadding={4}
        spotlightClicks={true}
        run={run}
        styles={{
          options: {
            zIndex: 10000,
          },
          tooltip: {
            overflow: "hidden",
          },
          tooltipFooter: {
            marginTop: 0,
          },
        }}
        locale={{
          back: "Back",
          last: "Close",
          next: "Next",
          skip: "Skip",
        }}
        floaterProps={{
          placement: "auto",
          disableFlip: false,
          modifiers: [
            {
              name: "flip",
              options: { fallbackPlacements: ["top", "right", "left", "bottom"] },
            },
            {
              name: "preventOverflow",
              options: { boundary: "window", tether: true, padding: 8 },
            },
          ],
        }}
        callback={(data) => {
          // eslint-disable-next-line 
          const { status, index, type } = data;

          // Close only when tour actually ends
          if (status === "finished" || status === "skipped") {
            stopTour();
            localStorage.removeItem(STORAGE_KEYS.SHOW_TOUR_GUIDE);
          }

          // Auto open sidebar on mobile for last steps
          // if (isMobile && type === "step:before") {
          //   const totalSteps = mobileSteps.length;
          //   const lastStepIndex = totalSteps - 1;
          //   const secondLastStepIndex = totalSteps - 2;

          //   // If we’re on last 2 steps → ensure sidebar is open
          //   if (index === secondLastStepIndex || index === lastStepIndex) {
          //     emit("openSidebar");
          //     setTimeout(() => {
          //       window.dispatchEvent(new Event("resize"));
          //     }, 200);
          //   }
          // }
        }}
      />
    </TourContext.Provider>
  );
}
